/**
 * Check-in Permissions & Feature Gating
 *
 * Defines check-in options available based on user role within the club.
 *
 * #2641 (Seth, 2026-08-05): all check-in METHODS are free on every tier,
 * permanently — "showing up must stay free." There is no backend tier gate on
 * any check-in path (`requireTier`/`requireTierForEvent` guard only GPX upload
 * and pace groups), so the old web-only QR lock (`isPaidTier`) promised an
 * upsell for a tier that can't even be purchased. The `clubTier` input and the
 * tier-rank machinery are gone.
 *
 * #2640: photo check-in removed entirely — it was a frontend-only phantom
 * (`handlePhotoCheckin` was a TODO stub; the backend `CheckinMethod` enum is
 * `gps | secret_word | qr_code | manual`, no photo route/column/migration).
 *
 * The only remaining lock is `admin_checkin_others`, which is ROLE-gated
 * (leaders/owners only), never tier-gated.
 */

import { type Event } from './api/events';

export type CheckinOption = 'gps' | 'qr' | 'admin_checkin_others';

export type LockedFeature = 'admin_checkin_others';

export interface CheckinPermissionsInput {
  event: Event;
  userRole: 'leader' | 'member';
}

export interface CheckinPermissions {
  enabled: boolean;
  availableOptions: CheckinOption[];
  lockedFeatures: LockedFeature[];
  canCheckinOthers: boolean;
  disabledReason?: string;
}

/**
 * #3017: a GPS check-in can only verify anything if the EVENT carries the full
 * coordinate trio. Without it the distance check has nothing to compare against,
 * so offering GPS takes the OS location permission to validate nothing — in
 * production that was 126 of 129 GPS events.
 *
 * The backend rejects this case outright (`POST /events/:id/checkins` returns a
 * "no location set" validation error), so a client that offers GPS here would be
 * rendering a control the API refuses — the Core Rule 48 hint/enforcement split.
 *
 * Mirrored in native as `mobile/src/lib/eventStatus.ts` `eventCanVerifyGps`.
 */
export function eventCanVerifyGps(event: Pick<Event, 'location_lat' | 'location_lng' | 'checkin_radius_m'>): boolean {
  // `!= null` (loose) on purpose: it rejects BOTH null and undefined. A strict
  // `!== null` would report an event whose fields are simply absent from the
  // payload as verifiable, because `undefined !== null` is true — failing open
  // on exactly the shape this predicate exists to catch.
  return (
    event.location_lat != null &&
    event.location_lng != null &&
    event.checkin_radius_m != null
  );
}

/**
 * Calculate what check-in options are available for a user.
 *
 * Policy:
 * - QR: always available on every tier (#2641). ('manual' is NOT a member option
 *   — it is leader-driven; see the availableOptions note below, #3031.)
 * - GPS: available on every tier, but only when the event can actually verify a
 *   position (#3017 — see `eventCanVerifyGps`).
 * - Admin check-in others: club leaders/owners ONLY (role-gated, not tier-gated).
 *
 * #2481: there is deliberately no event-creator branch on admin check-in.
 * `POST /events/:id/checkins/admin` rejects anyone whose CURRENT club role is
 * not leader/owner (`events.ts:2160`) — no `owner_id` escape hatch — so
 * granting the option to a creator would render a control the API refuses
 * (Core Rule 48). The input carried an `isEventCreator` flag sourced from a
 * field the API never returned, so the branch was permanently dead; it was
 * removed rather than activated (product decision, Seth 2026-07-26).
 */
export function calculateCheckinPermissions(input: CheckinPermissionsInput): CheckinPermissions {
  const { event, userRole } = input;

  // #3031: `manual` is NOT a member self-check-in option. `checkin_method:'manual'`
  // means a LEADER checks attendees in via the role-gated admin endpoint
  // (migration 0017_checkin_method.sql: "Leaders manually check in attendees";
  // LeaderCheckInPanel: "Attendees cannot self-check-in"). The old member "Manual"
  // button had no valid wire source — it sent `source:'gps'` with no coordinates
  // and 400'd on every event — so it is removed rather than made to work, which
  // would be a self-check-in authorization bypass on manual events.
  //
  // #3017 adds the GPS coordinate precondition. The remaining gps/qr list is still
  // hardcoded and still ignores `event.checkin_method` — that is #3002, a separate
  // defect in the same area, deliberately not fixed here.
  const availableOptions: CheckinOption[] = eventCanVerifyGps(event)
    ? ['gps', 'qr']
    : ['qr'];
  const lockedFeatures: LockedFeature[] = [];

  const canCheckinOthers = userRole === 'leader';
  if (canCheckinOthers) {
    availableOptions.push('admin_checkin_others');
  } else {
    lockedFeatures.push('admin_checkin_others');
  }

  return {
    enabled: true,
    availableOptions,
    lockedFeatures,
    canCheckinOthers,
  };
}

export type CheckinIconKey = 'map-pin' | 'smartphone' | 'hand' | 'users';

export function getCheckinOptionDescription(option: CheckinOption): {
  title: string;
  description: string;
  iconKey: CheckinIconKey;
} {
  switch (option) {
    case 'gps':
      return {
        title: 'GPS Check-in',
        description: 'Use your location to check in',
        iconKey: 'map-pin',
      };
    case 'qr':
      return {
        title: 'QR Code',
        description: 'Scan event QR code',
        iconKey: 'smartphone',
      };
    case 'admin_checkin_others':
      return {
        title: 'Check In Others',
        description: 'Admin check-in for attendees',
        iconKey: 'users',
      };
  }
}
